import React, { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useLeftNavStore } from './left-nav-store'
import { SlideOutNavItem } from './SlideOutNavItem'
import { SlideOutNavGroup } from './SlideOutNavGroup'
import { resolveChildren, isNavItemEnabled } from './useNavItems'
import { useNavItems } from './NavItemsContext'
import { featureFlagService } from 'utils/featureFlagService'
import { useAuth } from 'context/authentication/useAuth'
import { cn } from 'components/UI/cn'

interface SlideOutPanelProps {
  className?: string
  $isOpen?: boolean
}

export const SlideOutPanel: React.FC<SlideOutPanelProps> = ({
  className,
  $isOpen = false,
}) => {
  const { isSlideOutOpen, activeSection, closeSlideOut } = useLeftNavStore()
  const featureFlags = featureFlagService()
  const { user, isAllowed } = useAuth()
  const { navItems } = useNavItems()
  const location = useLocation()

  const activeItem = useMemo(
    () => navItems.find((item) => item.id === activeSection),
    [navItems, activeSection]
  )

  const title = activeItem?.label ?? ''

  // Close on escape key
  useEffect(() => {
    if (!isSlideOutOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeSlideOut()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isSlideOutOpen, closeSlideOut])

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement
    if (
      !target.closest('[data-slide-out-panel]') &&
      !target.closest('[data-left-nav]')
    ) {
      closeSlideOut()
    }
  }, [])

  useEffect(() => {
    if (!isSlideOutOpen) return

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isSlideOutOpen, handleClickOutside])

  const renderContent = () => {
    if (!activeItem) return null

    if (activeItem.isLoading) {
      return <div className="p-4 text-sm text-[#6e6e6e]">Loading...</div>
    }

    const children = resolveChildren(activeItem)
    if (!children?.length) return null

    return (
      <ul className="list-none m-0 p-0 flex flex-col gap-1.5">
        {children
          .filter(
            (child) =>
              isNavItemEnabled(child, featureFlags, user) &&
              isAllowed(child.minimumRole ?? 'Standard')
          )
          .map((child) =>
            child.children?.length ? (
              <SlideOutNavGroup
                key={child.id}
                label={child.label}
                description={child.description}
                items={child.children
                  .filter((sub) => isNavItemEnabled(sub, featureFlags, user))
                  .map((sub) => ({
                    label: sub.label,
                    path: sub.path,
                  }))}
                section={activeItem.id}
                defaultExpanded={location.pathname.includes(child.path)}
                isActiveGroup={location.pathname.includes(child.path)}
                keepOpen
              />
            ) : (
              <SlideOutNavItem
                key={child.path}
                label={child.label}
                path={child.path}
                section={activeItem.id}
                minimumRole={child.minimumRole}
                keepOpen
              />
            )
          )}
      </ul>
    )
  }

  return (
    <aside
      className={cn(
        'bg-[var(--slide-out-bg)] transition-all duration-200 ease-in-out flex flex-col rounded-l-lg overflow-hidden',
        $isOpen
          ? 'w-[var(--slide-out-panel-width)] min-w-[var(--slide-out-panel-width)]'
          : 'w-0 min-w-0',
        className
      )}
      role="navigation"
      aria-label={`${title} navigation`}
      data-slide-out-panel
    >
      <div className="w-[var(--slide-out-panel-width)] flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center gap-1 p-3 opacity-80">
          <div className="flex items-center justify-center p-1 text-[#191d23] [&_svg]:w-6 [&_svg]:h-6">
            {activeItem?.icon}
          </div>
          <h2 className="m-0 flex items-center text-md font-normal leading-[1.5rem] text-[#191d23] uppercase tracking-[0.05em]">
            {title}
          </h2>
        </div>

        {/* Divider */}
        <div className="h-px bg-[rgba(25,29,35,0.15)] mx-3 mb-3" />

        {/* Content */}
        <nav className="flex-1 overflow-y-auto px-3 pb-3">
          {renderContent()}
        </nav>
      </div>
    </aside>
  )
}
